import Map from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { LineString, Point } from 'ol/geom';
import { Fill, Stroke, Style, Circle as CircleStyle } from 'ol/style';
import { FeatureLike } from 'ol/Feature';
import { toLonLat } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import GeoJSON from 'ol/format/GeoJSON';
import { OsmMapApiService, friendlyOsmOverlayStatus } from '../services/osm-map-api.service';
import { Subscription } from 'rxjs';

export const OSM_NODES_MIN_ZOOM = 16;
export const OSM_MAX_BBOX_AREA_SQ_DEG = 0.25;

export function clampOsmBboxArea(
  west: number,
  south: number,
  east: number,
  north: number,
  maxArea: number = OSM_MAX_BBOX_AREA_SQ_DEG
): { west: number; south: number; east: number; north: number } {
  let w = west;
  let s = south;
  let e = east;
  let n = north;
  const area = (e - w) * (n - s);
  if (area <= maxArea) return { west: w, south: s, east: e, north: n };
  const scale = Math.sqrt(maxArea / area) * 0.98;
  const cx = (w + e) / 2;
  const cy = (s + n) / 2;
  const hw = ((e - w) / 2) * scale;
  const hh = ((n - s) / 2) * scale;
  return { west: cx - hw, south: cy - hh, east: cx + hw, north: cy + hh };
}

export function osmNetworkStyle(feature: FeatureLike, resolution?: number): Style | Style[] {
  const kind = String(feature.get('feature_kind') || 'road');
  const zoomScale = resolution != null ? Math.max(0.75, Math.min(2.2, 100 / resolution)) : 1;

  if (kind === 'waterway') {
    const ww = String(feature.get('waterway') || 'stream');
    const w = (ww === 'river' ? 5 : ww === 'canal' ? 4 : 2.5) * zoomScale;
    return new Style({
      stroke: new Stroke({
        color: '#4da3ff',
        width: w,
        lineCap: 'round',
        lineJoin: 'round',
      }),
    });
  }

  const hw = String(feature.get('highway') || 'road');
  const widths: Record<string, number> = {
    motorway: 7,
    trunk: 6,
    primary: 5.5,
    secondary: 5,
    tertiary: 4.5,
    residential: 4,
    unclassified: 4,
    service: 3,
    track: 3,
    path: 2.5,
    footway: 2,
    pedestrian: 2.5,
    cycleway: 2.5,
    living_street: 3.5,
  };
  const inner = (widths[hw] || 4) * zoomScale;
  const outer = inner + Math.max(2, inner * 0.35);
  const strokeOpts = { lineCap: 'round' as const, lineJoin: 'round' as const };

  return [
    new Style({ stroke: new Stroke({ color: '#3d3d3d', width: outer, ...strokeOpts }) }),
    new Style({ stroke: new Stroke({ color: '#ffffff', width: inner, ...strokeOpts }) }),
  ];
}

export function osmNodeStyle(zoom: number): Style | undefined {
  if (zoom < OSM_NODES_MIN_ZOOM) return undefined;
  const radius = zoom >= 19 ? 4.5 : zoom >= 18 ? 4 : zoom >= 17 ? 3.5 : 3;
  return new Style({
    image: new CircleStyle({
      radius,
      fill: new Fill({ color: '#ffffff' }),
      stroke: new Stroke({ color: '#3a3a3a', width: 1.5 }),
    }),
  });
}

export interface OsmNetworkOverlayOptions {
  zIndexNetwork?: number;
  zIndexNodes?: number;
  onStatus?: (message: string) => void;
}

/** OSM Map API vector overlay — iD-style roads, waterways, and vertex nodes. */
export class OsmNetworkOverlay {
  private map?: Map;
  private readonly format = new GeoJSON();
  private readonly networkSource = new VectorSource();
  private readonly nodesSource = new VectorSource();
  private readonly networkLayer: VectorLayer<VectorSource>;
  private readonly nodesLayer: VectorLayer<VectorSource>;
  private moveEndKey?: EventsKey;
  private resolutionKey?: EventsKey;
  private centerKey?: EventsKey;
  private nodeResolutionKey?: EventsKey;
  private loadTimer?: ReturnType<typeof setTimeout>;
  private nodeRebuildTimer?: ReturnType<typeof setTimeout>;
  private fetchSub?: Subscription;
  private requestSeq = 0;
  private lastBboxKey = '';
  private nodesWereVisible = false;
  private enabled = true;
  private networkVisible = true;
  private nodesVisible = true;
  roadCount = 0;
  nodeCount = 0;

  constructor(
    private readonly osmMapApi: OsmMapApiService,
    private readonly options: OsmNetworkOverlayOptions = {}
  ) {
    const zNet = options.zIndexNetwork ?? 6;
    const zNodes = options.zIndexNodes ?? 7;
    this.networkLayer = new VectorLayer({
      source: this.networkSource,
      zIndex: zNet,
      properties: { name: 'osm_road_network' },
      style: (feature, resolution) => osmNetworkStyle(feature, resolution),
    });
    this.nodesLayer = new VectorLayer({
      source: this.nodesSource,
      zIndex: zNodes,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      properties: { name: 'osm_road_nodes' },
      style: () => {
        const zoom = this.map?.getView().getZoom() ?? 0;
        return osmNodeStyle(zoom);
      },
    });
  }

  attach(map: Map): void {
    if (this.map) this.detach();
    this.map = map;
    map.addLayer(this.networkLayer);
    map.addLayer(this.nodesLayer);
    this.attachListeners();
    this.scheduleLoad(200);
  }

  detach(): void {
    this.clearTimers();
    this.fetchSub?.unsubscribe();
    this.fetchSub = undefined;
    if (this.map) {
      if (this.moveEndKey) unByKey(this.moveEndKey);
      if (this.resolutionKey) unByKey(this.resolutionKey);
      if (this.centerKey) unByKey(this.centerKey);
      if (this.nodeResolutionKey) unByKey(this.nodeResolutionKey);
      this.moveEndKey = undefined;
      this.resolutionKey = undefined;
      this.centerKey = undefined;
      this.nodeResolutionKey = undefined;
      this.map.removeLayer(this.networkLayer);
      this.map.removeLayer(this.nodesLayer);
    }
    this.networkSource.clear();
    this.nodesSource.clear();
    this.lastBboxKey = '';
    this.nodesWereVisible = false;
    this.map = undefined;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.applyVisibility();
    if (enabled) {
      this.scheduleLoad(100);
    } else {
      this.setStatus('');
    }
  }

  setNetworkVisible(visible: boolean): void {
    this.networkVisible = visible;
    this.applyVisibility();
    if (visible && this.enabled) {
      this.scheduleLoad(100);
    }
  }

  setNodesVisible(visible: boolean): void {
    this.nodesVisible = visible;
    this.applyVisibility();
    if (visible) {
      this.scheduleNodeRebuild(0);
    }
  }

  isNetworkVisible(): boolean {
    return this.networkVisible;
  }

  isNodesVisible(): boolean {
    return this.nodesVisible;
  }

  setNetworkOpacity(opacity: number): void {
    this.networkLayer.setOpacity(opacity);
  }

  setNodesOpacity(opacity: number): void {
    this.nodesLayer.setOpacity(opacity);
  }

  private applyVisibility(): void {
    const on = this.enabled;
    this.networkLayer.setVisible(on && this.networkVisible);
    this.nodesLayer.setVisible(on && this.networkVisible && this.nodesVisible);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  refresh(): void {
    this.lastBboxKey = '';
    this.scheduleLoad(0);
  }

  private attachListeners(): void {
    if (!this.map) return;
    const view = this.map.getView();
    this.moveEndKey = this.map.on('moveend', () => this.scheduleLoad());
    this.nodeResolutionKey = view.on('change:resolution', () => this.scheduleNodeRebuild());
  }

  private scheduleLoad(delayMs = 450): void {
    if (!this.enabled || !this.map) return;
    if (this.loadTimer) clearTimeout(this.loadTimer);
    this.loadTimer = setTimeout(() => this.load(), delayMs);
  }

  private scheduleNodeRebuild(delayMs = 60): void {
    if (!this.map) return;
    if (this.nodeRebuildTimer) clearTimeout(this.nodeRebuildTimer);
    const willShow = this.nodesShouldShow();
    const immediate = willShow !== this.nodesWereVisible;
    this.nodeRebuildTimer = setTimeout(() => {
      this.nodesWereVisible = willShow;
      this.rebuildNodes(this.networkSource.getFeatures() as Feature[]);
      this.nodesLayer.changed();
      this.map?.render();
    }, immediate ? 0 : delayMs);
  }

  private nodesShouldShow(): boolean {
    if (!this.enabled || !this.nodesVisible || !this.networkVisible) return false;
    const zoom = this.map?.getView().getZoom();
    return zoom != null && zoom >= OSM_NODES_MIN_ZOOM;
  }

  private load(): void {
    if (!this.enabled || !this.map) return;
    const size = this.map.getSize();
    if (!size || size[0] < 10 || size[1] < 10) return;

    const bbox = this.mapBboxWgs84();
    if (!bbox) return;

    const bboxKey = [bbox.west, bbox.south, bbox.east, bbox.north]
      .map((v) => v.toFixed(5))
      .join(',');
    if (bboxKey === this.lastBboxKey && this.networkSource.getFeatures().length > 0) {
      this.scheduleNodeRebuild(0);
      return;
    }

    const zoom = this.map.getView().getZoom();
    const seq = ++this.requestSeq;
    this.setStatus('Loading OSM road network…');

    this.fetchSub?.unsubscribe();
    this.fetchSub = this.osmMapApi.fetchRoadNetwork({ ...bbox, zoom: zoom ?? undefined }).subscribe({
      next: (res) => {
        if (seq !== this.requestSeq || !this.map || !this.enabled) return;
        if (!res.success || !res.data?.features?.length) {
          this.setStatus('No OSM highways in this extent');
          return;
        }
        const feats = this.format.readFeatures(
          { type: 'FeatureCollection', features: res.data.features },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        );
        this.networkSource.clear();
        this.networkSource.addFeatures(feats);
        this.rebuildNodes(feats);
        this.nodesWereVisible = this.nodesShouldShow();
        this.roadCount = feats.filter((f) => f.get('feature_kind') !== 'waterway').length;
        const waterCount = feats.filter((f) => f.get('feature_kind') === 'waterway').length;
        this.lastBboxKey = bboxKey;
        this.setStatus(
          waterCount > 0
            ? `OSM: ${this.roadCount} roads · ${waterCount} waterways`
            : `OSM: ${this.roadCount} road segments`
        );
        this.networkLayer.changed();
        this.nodesLayer.changed();
        this.map.render();
      },
      error: (err) => {
        if (seq !== this.requestSeq) return;
        this.setStatus(friendlyOsmOverlayStatus(err));
      },
    });
  }

  private mapBboxWgs84(): ReturnType<typeof clampOsmBboxArea> | null {
    if (!this.map) return null;
    const extent = this.map.getView().calculateExtent(this.map.getSize() || [800, 600]);
    const sw = toLonLat([extent[0], extent[1]]);
    const ne = toLonLat([extent[2], extent[3]]);
    return clampOsmBboxArea(sw[0], sw[1], ne[0], ne[1]);
  }

  private rebuildNodes(networkFeatures: Feature[]): void {
    this.nodesSource.clear();
    if (!this.nodesShouldShow() || !networkFeatures.length) return;

    const zoom = this.map?.getView().getZoom() ?? OSM_NODES_MIN_ZOOM;
    const dedupDecimals = zoom >= 19 ? 2 : zoom >= 18 ? 1 : 0;
    const seen = new Set<string>();

    for (const f of networkFeatures) {
      const geom = f.getGeometry();
      if (!(geom instanceof LineString)) continue;
      for (const c of geom.getCoordinates()) {
        const key = `${c[0].toFixed(dedupDecimals)},${c[1].toFixed(dedupDecimals)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        this.nodesSource.addFeature(new Feature(new Point(c)));
      }
    }
    this.nodeCount = this.nodesSource.getFeatures().length;
  }

  private setStatus(message: string): void {
    this.options.onStatus?.(message);
  }

  private clearTimers(): void {
    if (this.loadTimer) clearTimeout(this.loadTimer);
    if (this.nodeRebuildTimer) clearTimeout(this.nodeRebuildTimer);
    this.loadTimer = undefined;
    this.nodeRebuildTimer = undefined;
  }
}
